import { uiText, useUiLanguage, currentLocale } from '@/i18n/interface';
import React, { useState, useEffect, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Bot, Send, User, AlertTriangle, CheckCircle, XCircle, Undo2 } from 'lucide-react';
import { toast } from '@/hooks/use-toast';

interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  queryId?: string;
}

interface QueryLog {
  id: string;
  query_text: string;
  query_type: string;
  success: boolean;
  error_message: string | null;
  created_at: string;
}

const AIAgent: React.FC = () => {
  useUiLanguage();
  const [messages, setMessages] = useState<ChatMessage[]>([
    {
      id: '1',
      role: 'assistant',
      content: 'Hello! I\'m your AI Database Agent. I can help you inspect and modify your Supabase database safely. What would you like to do?',
      timestamp: new Date()
    }
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    query: string;
    description: string;
  }>({ open: false, query: '', description: '' });
  const [queryLogs, setQueryLogs] = useState<QueryLog[]>([]);
  const scrollAreaRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    fetchQueryLogs();
  }, []);

  useEffect(() => {
    // Scroll to bottom when new messages are added
    if (scrollAreaRef.current) {
      scrollAreaRef.current.scrollTop = scrollAreaRef.current.scrollHeight;
    }
  }, [messages]);

  const fetchQueryLogs = async () => {
    try {
      const { data, error } = await supabase
        .from('query_logs')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(10);

      if (error) throw error;
      setQueryLogs(data || []);
    } catch (error) {
      console.error('Error fetching query logs:', error);
    }
  };

  const handleSendMessage = async () => {
    if (!input.trim() || loading) return;

    const userMessage: ChatMessage = {
      id: Date.now().toString(),
      role: 'user',
      content: input,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setLoading(true);

    try {
      // Call AI agent edge function
      const { data, error } = await supabase.functions.invoke('ai-database-agent', {
        body: {
          message: input,
          conversation_history: messages.slice(-5) // Send last 5 messages for context
        }
      });

      if (error) throw error;

      const assistantMessage: ChatMessage = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: data.response,
        timestamp: new Date()
      };

      setMessages(prev => [...prev, assistantMessage]);

      // If there's a query to confirm, show confirmation dialog
      if (data.requires_confirmation) {
        setConfirmDialog({
          open: true,
          query: data.proposed_query,
          description: data.query_description
        });
      }
    } catch (error) {
      console.error('Error with AI agent:', error);
      const errorMessage: ChatMessage = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: 'Sorry, I encountered an error processing your request. Please try again.',
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmQuery = async () => {
    try {
      const { data, error } = await supabase.functions.invoke('execute-database-query', {
        body: {
          query: confirmDialog.query,
          query_type: 'admin_modification'
        }
      });

      if (error) throw error;

      const resultMessage: ChatMessage = {
        id: Date.now().toString(),
        role: 'assistant',
        content: data.success
          ? `✅ Query executed successfully! ${data.message || ''}`
          : `❌ Query failed: ${data.error}`,
        timestamp: new Date(),
        queryId: data.query_id
      };

      setMessages(prev => [...prev, resultMessage]);
      setConfirmDialog({ open: false, query: '', description: '' });
      fetchQueryLogs();

      toast({
        title: data.success ? 'Query Executed' : 'Query Failed',
        description: data.success ? 'Database updated successfully' : data.error,
        variant: data.success ? 'default' : 'destructive'
      });
    } catch (error) {
      console.error('Error executing query:', error);
      toast({
        title: uiText("Error"),
        description: uiText("Failed to execute query"),
        variant: 'destructive'
      });
    }
  };

  const handleRollback = async (queryId: string) => {
    try {
      const { data, error } = await supabase.functions.invoke('rollback-database-query', {
        body: { query_id: queryId }
      });

      if (error) throw error;

      toast({
        title: data.success ? 'Rollback Successful' : 'Rollback Failed',
        description: data.message,
        variant: data.success ? 'default' : 'destructive'
      });

      fetchQueryLogs();
    } catch (error) {
      console.error('Error rolling back:', error);
      toast({
        title: uiText("Error"),
        description: uiText("Failed to rollback query"),
        variant: 'destructive'
      });
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 h-[600px]">
      {/* Chat Interface */}
      <div className="lg:col-span-2">
        <Card className="h-full flex flex-col">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Bot className="h-5 w-5" />
              {uiText("AI Database Agent")}</CardTitle>
          </CardHeader>
          <CardContent className="flex-1 flex flex-col">
            <ScrollArea className="flex-1 pr-4" ref={scrollAreaRef}>
              <div className="space-y-4">
                {messages.map((message) => (
                  <div key={message.id} className={`flex gap-3 ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                    <div className={`flex gap-2 max-w-[80%] ${message.role === 'user' ? 'flex-row-reverse' : 'flex-row'}`}>
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                        message.role === 'user'
                          ? 'bg-primary text-primary-foreground'
                          : 'bg-muted text-muted-foreground'
                      }`}>
                        {message.role === 'user' ? <User className="h-4 w-4" /> : <Bot className="h-4 w-4" />}
                      </div>
                      <div className={`rounded-lg p-3 ${
                        message.role === 'user'
                          ? 'bg-primary text-primary-foreground'
                          : 'bg-muted'
                      }`}>
                        <p className="whitespace-pre-wrap">{message.content}</p>
                        {message.queryId && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="mt-2"
                            onClick={() => handleRollback(message.queryId!)}
                          >
                            <Undo2 className="h-3 w-3 mr-1" />
                            {uiText("Rollback")}</Button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
                {loading && (
                  <div className="flex gap-3 justify-start">
                    <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center">
                      <Bot className="h-4 w-4" />
                    </div>
                    <div className="bg-muted rounded-lg p-3">
                      <div className="flex items-center gap-2">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary"></div>
                        <span>{uiText("Thinking...")}</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </ScrollArea>

            {/* Input */}
            <div className="flex gap-2 mt-4">
              <Input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder={uiText("Ask me to inspect or modify your database...")}
                disabled={loading}
              />
              <Button onClick={handleSendMessage} disabled={loading || !input.trim()}>
                <Send className="h-4 w-4" />
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Query Logs */}
      <div>
        <Card className="h-full">
          <CardHeader>
            <CardTitle className="text-sm">{uiText("Recent Query Logs")}</CardTitle>
          </CardHeader>
          <CardContent>
            <ScrollArea className="h-[400px]">
              <div className="space-y-3">
                {queryLogs.map((log) => (
                  <div key={log.id} className="p-3 border rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      {log.success ? (
                        <CheckCircle className="h-4 w-4 text-green-600" />
                      ) : (
                        <XCircle className="h-4 w-4 text-red-600" />
                      )}
                      <Badge variant={log.success ? 'default' : 'destructive'}>
                        {log.query_type}
                      </Badge>
                    </div>
                    <p className="text-xs font-mono bg-muted p-2 rounded">
                      {log.query_text.substring(0, 100)}...
                    </p>
                    {log.error_message && (
                      <p className="text-xs text-red-600 mt-1">{log.error_message}</p>
                    )}
                    <p className="text-xs text-muted-foreground mt-1">
                      {new Date(log.created_at).toLocaleString(currentLocale())}
                    </p>
                  </div>
                ))}
              </div>
            </ScrollArea>
          </CardContent>
        </Card>
      </div>

      {/* Confirmation Dialog */}
      <Dialog open={confirmDialog.open} onOpenChange={(open) => setConfirmDialog(prev => ({ ...prev, open }))}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-yellow-600" />
              {uiText("Confirm Database Query")}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p>{confirmDialog.description}</p>
            <div className="bg-muted p-3 rounded-lg">
              <p className="font-mono text-sm">{confirmDialog.query}</p>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setConfirmDialog({ open: false, query: '', description: '' })}>
                {uiText("Cancel")}</Button>
              <Button onClick={handleConfirmQuery}>
                {uiText("Execute Query")}</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AIAgent;